import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { FolderOpen, Upload, Download, Trash2, Settings, Loader2, Save } from 'lucide-react';
import { SmtpSelfTestPanel } from '../SmtpSelfTestPanel';
import { openAuthed } from '../../../lib/authSession';
import EmailDeliveryPanel from '../../../components/EmailDeliveryPanel';

export const CommonFilesTab = ({ ctx }) => {
  const {
    commonFiles, commonFilesCategory, setCommonFilesCategory, setShowUploadCommonModal,
    getCategoryLabel, getFileIcon, formatFileSize, handleDeleteCommonFile, apiUrl,
  } = ctx;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-[#0077B6]" />
            Common Files Management
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg bg-gray-100 p-1">
              {['all', 'company', 'mortgage', 'insurance_investments'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setCommonFilesCategory(cat)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${commonFilesCategory === cat ? 'bg-white shadow text-[#0077B6] font-medium' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  {cat === 'all' ? 'All' : getCategoryLabel(cat)}
                </button>
              ))}
            </div>
            <Button onClick={() => setShowUploadCommonModal(true)} className="bg-[#0077B6]">
              <Upload className="h-4 w-4 mr-2" />Upload Files
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          <strong>Access Control:</strong> Company files are visible to all agents. Mortgage files are only visible to mortgage licensees. Insurance & Investments files are only visible to insurance/investments licensees.
        </p>
      </CardHeader>
      <CardContent>
        {commonFiles.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <FolderOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No files in this category</p>
            <p className="text-sm">Upload company, mortgage, or insurance documents for agents to access</p>
          </div>
        ) : (
          <div className="space-y-2">
            {commonFiles.map(file => (
              <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 group">
                <div className="flex items-center gap-4 min-w-0">
                  {getFileIcon(file.file_type)}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-800">{file.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        file.category === 'company' ? 'bg-blue-100 text-blue-700' :
                        file.category === 'mortgage' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {getCategoryLabel(file.category)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{file.filename} • {formatFileSize(file.file_size)} • {new Date(file.created_at).toLocaleDateString()}</p>
                    {file.description && <p className="text-sm text-gray-600 mt-1">{file.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href={`${apiUrl}/api/common-files/download/${file.id}`} onClick={openAuthed} target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4 mr-1" />Download
                    </a>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteCommonFile(file.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const SettingsTab = ({ ctx }) => {
  const { siteSettings, setSiteSettings, handleSaveSettings, savingSettings, password } = ctx;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-[#0077B6]" />
          Site Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">UTFI Intranet URL</label>
          <p className="text-sm text-gray-500 mb-2">Enter the external URL for the UTFI Intranet. This link will appear in the agent portal.</p>
          <div className="flex gap-3">
            <input
              type="url"
              value={siteSettings.intranet_url}
              onChange={(e) => setSiteSettings({ ...siteSettings, intranet_url: e.target.value })}
              placeholder="https://intranet.utmostfinancial.ca"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
            />
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-[#0077B6]">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Officer Email</label>
          <p className="text-sm text-gray-500 mb-2">Pre-filled when agents email a Final Compliance Package from a client folder. Separate several addresses with commas.</p>
          <div className="flex gap-3">
            <input
              type="text"
              value={siteSettings.compliance_officer_email || ''}
              onChange={(e) => setSiteSettings({ ...siteSettings, compliance_officer_email: e.target.value })}
              placeholder="compliance@utmostfinancial.ca"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
              data-testid="settings-compliance-email"
            />
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-[#0077B6]" data-testid="settings-save-compliance-email">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alert Email (bounce / delivery failures)</label>
          <p className="text-sm text-gray-500 mb-2">Gets an immediate alert whenever an outgoing email fails (max one per 30 min per error type) plus a 9:00 AM ET daily report when anything failed in the last 24h. Defaults to the mailbox login when empty.</p>
          <div className="flex gap-3">
            <input
              type="email"
              value={siteSettings.alert_email || ''}
              onChange={(e) => setSiteSettings({ ...siteSettings, alert_email: e.target.value })}
              placeholder="you@utmostfinancial.ca"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
              data-testid="settings-alert-email"
            />
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-[#0077B6]" data-testid="settings-save-alert-email">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>

        <EmailDeliveryPanel apiUrl={process.env.REACT_APP_BACKEND_URL} />

        <SmtpSelfTestPanel adminPassword={password} />
      </CardContent>
    </Card>
  );
};
